import { t, Trans } from "@lingui/macro";
import cx from "classnames";
import { ReactNode, useMemo } from "react";

import { formatDeltaUsd, formatPercentage } from "lib/numbers";
import { getPositiveOrNegativeClass } from "lib/utils";
import { FeeItem } from "sdk/types/fees";
import { bigMath } from "sdk/utils/bigmath";

import StatsTooltipRow from "components/StatsTooltip/StatsTooltipRow";
import { SyntheticsInfoRow } from "components/Synthetics/SyntheticsInfoRow";
import Tooltip from "components/Tooltip/Tooltip";

import { Operation } from "../GmSwapBox/types";
import "./GmFees.scss";

type Props = {
  totalFees?: FeeItem;
  swapFee?: FeeItem;
  swapPriceImpact?: FeeItem;
  uiFee?: FeeItem;
  shiftFee?: FeeItem;
  operation: Operation;
};

export function GmFees(p: Props) {
  const totalFeesUsd = p.totalFees?.deltaUsd;

  let value: ReactNode = useMemo(() => {
    const operationTexts = {
      [Operation.Deposit]: t`buy`,
      [Operation.Withdrawal]: t`sell`,
      [Operation.Shift]: t`shift`,
    };

    const operationText = operationTexts[p.operation];

    if (p.totalFees?.deltaUsd === undefined) {
      return "-";
    } else if (
      bigMath.abs(p.swapPriceImpact?.deltaUsd ?? 0n) > 0 ||
      p.swapFee ||
      bigMath.abs(p.uiFee?.deltaUsd ?? 0n) > 0
    ) {
      return (
        <Tooltip
          className="GmFees-tooltip"
          handle={
            <span>
              <span
                className={cx({
                  "text-green-300": p.swapPriceImpact?.deltaUsd !== undefined && p.swapPriceImpact.deltaUsd > 0,
                })}
              >
                {p.swapPriceImpact?.deltaUsd !== undefined && p.swapPriceImpact.deltaUsd > 0 ? "+" : "-"}
                {formatPercentage(p.swapPriceImpact?.precisePercentage, {
                  bps: false,
                  displayDecimals: 3,
                })}
              </span>
              {" / "}
              <span
                className={cx({ "text-green-300": p.totalFees?.deltaUsd !== undefined && p.totalFees.deltaUsd > 0 })}
              >
                {p.totalFees?.deltaUsd !== undefined && p.totalFees.deltaUsd > 0 ? "+" : "-"}
                {formatPercentage(p.totalFees?.precisePercentage, {
                  bps: false,
                  displayDecimals: 3,
                })}
              </span>
            </span>
          }
          position="top-end"
          renderContent={() => (
            <div>
              {bigMath.abs(p.swapPriceImpact?.deltaUsd ?? 0n) > 0 && (
                <StatsTooltipRow
                  label={
                    <div>
                      <div>{t`Price Impact`}:</div>
                      <div>
                        (
                        {formatPercentage(p.swapPriceImpact?.precisePercentage, {
                          bps: false,
                          displayDecimals: 3,
                        })}{" "}
                        of {operationText} amount)
                      </div>
                    </div>
                  }
                  value={formatDeltaUsd(p.swapPriceImpact?.deltaUsd)!}
                  showDollar={false}
                  textClassName={getPositiveOrNegativeClass(
                    p.swapPriceImpact === undefined ? undefined : p.swapPriceImpact.deltaUsd
                  )}
                />
              )}

              {p.swapFee && (
                <>
                  <StatsTooltipRow
                    label={
                      <div>
                        <div>{p.operation === Operation.Deposit ? t`Buy Fee` : t`Sell Fee`}:</div>
                        <div>
                          (
                          {formatPercentage(p.swapFee.precisePercentage, {
                            bps: false,
                            displayDecimals: 3,
                          })}{" "}
                          of {operationText} amount)
                        </div>
                      </div>
                    }
                    value={formatDeltaUsd(p.swapFee.deltaUsd)!}
                    showDollar={false}
                    textClassName={getPositiveOrNegativeClass(p.swapFee.deltaUsd)}
                  />
                </>
              )}

              {bigMath.abs(p.uiFee?.deltaUsd ?? 0n) > 0 && (
                <StatsTooltipRow
                  label={
                    <div>
                      <div>{t`UI Fee`}:</div>
                      <div>
                        (
                        {formatPercentage(p.uiFee?.precisePercentage, {
                          bps: false,
                          displayDecimals: 3,
                        })}{" "}
                        of {operationText} amount)
                      </div>
                    </div>
                  }
                  value={formatDeltaUsd(p.uiFee?.deltaUsd)!}
                  showDollar={false}
                  textClassName="text-red-alpha-100"
                />
              )}

              {p.shiftFee !== undefined && (
                <StatsTooltipRow
                  label={
                    <div>
                      <div>{t`Shift Fee`}:</div>
                      <div>
                        (
                        {formatPercentage(p.shiftFee.precisePercentage, {
                          bps: false,
                          displayDecimals: 3,
                        })}{" "}
                        of {operationText} amount)
                      </div>
                    </div>
                  }
                  value={formatDeltaUsd(p.shiftFee.deltaUsd)!}
                  showDollar={false}
                />
              )}
            </div>
          )}
        />
      );
    }
    return (
      <span className={cx({ positive: totalFeesUsd !== undefined && totalFeesUsd > 0 })}>
        {formatPercentage(p.swapPriceImpact?.precisePercentage, {
          bps: false,
          displayDecimals: 3,
        })}
        {" / "}
        {formatPercentage(p.totalFees?.precisePercentage, {
          bps: false,
          displayDecimals: 3,
        })}
      </span>
    );
  }, [
    p.operation,
    p.shiftFee,
    p.swapFee,
    p.swapPriceImpact,
    p.totalFees?.deltaUsd,
    p.totalFees?.precisePercentage,
    p.uiFee?.precisePercentage,
    p.uiFee?.deltaUsd,
    totalFeesUsd,
  ]);

  return (
    <SyntheticsInfoRow
      labelClassName="!text-[13px] !font-medium !text-gray-lighter"
      valueClassName="text-[13px] font-medium text-white"
      label={<Trans>Price Impact / Fees</Trans>}
      value={value}
      className="w-max flex-col !gap-12"
    />
  );
}
